import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings, resolveImageUrl } from "@/context/SettingsContext";
import { LogoMark } from "@/components/Logo";
import { Upload, Image as ImageIcon, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { FontSection, ContentSection } from "@/pages/AdminContent";
import { EventLinksSection } from "@/pages/AdminEvents";
import { UsersSection } from "@/pages/AdminUsers";

export default function AdminPage() {
  const { user } = useAuth();
  const { settings, loading, update: updateSettings, refresh } = useSettings();

  if (user?.role !== "admin") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="tavern-card p-8 max-w-md text-center">
          <Shield size={28} className="text-crimson-bright mx-auto mb-3" />
          <h2 className="font-display tracking-[0.3em] uppercase text-lg text-white mb-2">Keeper only</h2>
          <p className="text-sm text-zinc-400">You need the tavern-keeper's key to enter this chamber.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 py-10 sm:py-16 max-w-5xl mx-auto space-y-10">
      <header>
        <div className="text-[10px] tracking-[0.5em] uppercase text-crimson-bright mb-3">Behind the bar</div>
        <h1 className="font-display font-black tracking-[0.08em] uppercase text-3xl sm:text-5xl text-white">
          Keeper<span className="text-crimson">&apos;</span>s Chamber
        </h1>
        <p className="text-zinc-400 mt-3 text-sm max-w-xl">
          Customize the tavern. Swap the sigil, rewrite the welcome, keep the fellowship informed.
        </p>
      </header>

      <LogoSection settings={settings} updateSettings={updateSettings} refresh={refresh} />
      {!loading && <FontSection settings={settings} updateSettings={updateSettings} />}
      {!loading && <ContentSection settings={settings} updateSettings={updateSettings} />}
      <EventLinksSection />
      <UsersSection />
    </div>
  );
}

function LogoSection({ settings, updateSettings, refresh }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await updateSettings({ logo_url: data.url });
      toast.success("Sigil updated. The tavern wears new colours.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Restore the original Ballhoot’s Tavern logo?")) return;
    await updateSettings({ logo_url: "" });
    toast.success("Sigil reset.");
    await refresh();
  };

  const current = preview || resolveImageUrl(settings.logo_url);

  return (
    <section className="tavern-card p-6 sm:p-8" data-testid="admin-logo-section">
      <div className="flex items-center gap-2 mb-6">
        <ImageIcon size={16} className="text-crimson-bright" />
        <h2 className="font-display tracking-[0.3em] uppercase text-sm text-white">The Sigil</h2>
      </div>
      <div className="grid sm:grid-cols-[160px_1fr] gap-6 items-center">
        <div className="flex items-center justify-center">
          {current ? (
            <div
              className="w-36 h-36 rounded-full overflow-hidden border"
              style={{ borderColor: "rgba(192,192,192,0.35)", background: "#0a0a0b" }}
            >
              <img src={current} alt="Logo preview" className="w-full h-full object-cover" data-testid="admin-logo-preview" />
            </div>
          ) : (
            <LogoMark size={144} />
          )}
        </div>
        <div className="space-y-3">
          <p className="text-sm text-zinc-400 leading-relaxed">
            PNG, JPEG, WEBP or GIF up to 10 MB. Looks best as a square image on a dark background.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={onFile}
            data-testid="admin-logo-input"
          />
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onPick}
              disabled={uploading}
              data-testid="admin-logo-upload"
              className="btn-crimson px-4 py-2 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload New Sigil"}
            </button>
            <button
              onClick={resetToDefault}
              data-testid="admin-logo-reset"
              className="btn-ghost px-4 py-2 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2"
            >
              <Trash2 size={14} /> Reset
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

